import axios from 'axios'
import cloneDeep from 'lodash.clonedeep'

import { castAttributesForCamelCase, castAttributesForSnakeCase } from '../../../../modules/string_converter'
import { i18n } from '../../../../plugins/i18n'

/**
 * その属性に設定可能な値と表示名
 * @typedef valueMetadata
 * @type { object }
 * @property { string } displayName
 * @property { string } value
 */

/**
 * 配列groupDataに入っているオブジェクト + 設定されている値(repeatedEnumValue,values)
 * @typedef groupData
 * @type { object }
 * @property { string } parent
 * @property { string } displayName
 * @property { string } groupDisplayName
 * @property { valueMetadata[] } valueMetadata
 * @property { 'BOOL' | 'ENUM' | 'REPEATED_ENUM' | 'URL' } valueType
 * @property { ?repeatedEnumValue | undefined } repeatedEnumValue - 値が設定されていてvalueType === 'REPEATED_ENUM'の時のみ存在する
 * @property { ?attributeValues | undefined } values - 値が設定されていてvalueType === 'REPEATED_ENUM'以外の時のみ存在する
 * @property { ?urlValue[] | undefined } uriValues - 値が設定されていてvalueType === 'URL'の時のみ存在する
 * @property { boolean } repeatable
 */

/**
 * @typedef repeatedEnumValue
 * @type { object }
 * @property { string[] } setValues
 * @property { string[] } unsetValues
 */

/**
 * @typedef attributeValues
 * @type { boolean[] | string[] }
 */

/**
 * @typedef urlValue
 * @type object
 * @property { string } url
 */

/**
 * @typedef attributeMetadata
 * @property { groupData } groupData
 * @property { string } displayName
 */

/**
 * @type {['BOOL', 'ENUM', 'REPEATED_ENUM', 'URL']}
 */
export const gmbLocationAttributeValueTypes = ['BOOL', 'ENUM', 'REPEATED_ENUM', 'URL']

export const companiesStoreGmbAttributes = {
  namespaced: true,
  state: {
    attributes: [],
    attributeMetadata: [],
    loading: false,
  },
  getters: {
    /**
     * valueTypeがURL以外の属性がセットされているかどうか
     * @return boolean
     */
    isAnyAttributesSet(state) {
      const attributesWithoutUrl = state.attributes.filter(attribute => attribute.valueType !== gmbLocationAttributeValueTypes[3])
      return attributesWithoutUrl.length !== 0
    },
    /**
     * valueTypeがURL以外の属性でカテゴリに対して操作できる属性があるかどうか
     * 宿泊系カテゴリ等には属性は設定できない
     * @return boolean
     */
    hasSettableAttributes(state, getters) {
      return getters.attributeMetadataExceptUrl.length > 0
    },
    /**
     * valueTypeがURL以外の属性
     * @return { attributeMetadata[] }
     */
    attributeMetadataExceptUrl(state, getters) {
      return getters.attributeMetadata.filter(metadata => {
        let bool = false
        metadata.groupData.forEach(v => {
          if (v.valueType !== gmbLocationAttributeValueTypes[3]) {
            bool = true
          }
        })
        return bool
      })
    },
    /**
     * valueTypeがURL以外の属性で、データがセットされているもの
     * @return { attributeMetadata[] }
     */
    attributesMetadataHasValuesWithoutUrl(state, getters) {
      return getters.attributeMetadataExceptUrl.map(metadata => {
        const data = cloneDeep(metadata)
        data.groupData = data.groupData.filter(v => {
          if (v.valueType === gmbLocationAttributeValueTypes[2]) {
            return v.repeatedEnumValue && (v.repeatedEnumValue.setValues.length || v.repeatedEnumValue.unsetValues.length)
          }
          return v.values && v.values.length
        })
        return data
      })
    },
    /**
     * @param state
     * @return { attributeMetadata[] }
     */
    attributeMetadata(state) {
      return state.attributeMetadata.map(metaItem => {
        const meta = cloneDeep(metaItem)
        meta.groupData = meta.groupData.map(item => {
          const data = cloneDeep(item)
          const userSetData = state.attributes.find(attribute => attribute.name === data.parent)
          let valueKeyName = ''
          switch (data.valueType) {
          case gmbLocationAttributeValueTypes[2]:
            valueKeyName = 'repeatedEnumValue'
            break
          case gmbLocationAttributeValueTypes[3]:
            valueKeyName = 'uriValues'
            break
          default:
            valueKeyName = 'values'
          }
          if (!userSetData) {
            delete data[valueKeyName]
            return data
          }
          data[valueKeyName] = userSetData[valueKeyName]
          return data
        })
        return meta
      })
    },
    urlGroupDataFlattenList(state, getters) {
      let arr = []
      getters.attributeMetadata.forEach(meta => {
        arr = meta.groupData.filter(groupData => groupData.valueType === gmbLocationAttributeValueTypes[3])
      })
      return arr
    },
  },
  mutations: {
    /**
     * @param state
     * @param { string[] } uriValues
     * @param { groupData } metadata
     */
    changeUrlAttribute(state, { uriValues, metadata }) {
      const target = state.attributes.find(attribute => attribute.name === metadata.parent)
      if (!target) {
        if (uriValues.length === 0) return
        const obj = {
          name: metadata.parent,
          valueType: metadata.valueType,
        }
        obj.uriValues = uriValues
        state.attributes.push(obj)
        return
      }
      const newAttributes = []
      for (let i = 0; i < state.attributes.length; i++) {
        const attribute = cloneDeep(state.attributes[i])
        if (attribute.name !== target.name) {
          newAttributes.push(attribute)
          continue
        }
        if (uriValues.length === 0) continue
        attribute.uriValues = uriValues
        newAttributes.push(attribute)
      }
      state.attributes = newAttributes
    },
    /**
     * @param state
     * @param { groupData } metadata
     */
    changeBoolAttribute(state, metadata) {
      const target = state.attributes.find(attribute => attribute.name === metadata.parent)
      if (!target) {
        const obj = {
          name: metadata.parent,
          valueType: metadata.valueType,
        }
        obj.values = [true]
        state.attributes.push(obj)
        return
      }
      const newAttributes = []
      for (let i = 0; i < state.attributes.length; i++) {
        const attribute = cloneDeep(state.attributes[i])
        if (attribute.name !== target.name) {
          newAttributes.push(attribute)
          continue
        }
        const value = attribute.values[0]
        if (value === true) {
          attribute.values = [false]
        } else if (value === false) {
          continue
        }
        newAttributes.push(attribute)
      }
      state.attributes = newAttributes
    },
    /**
     * @param state
     * @param { 'noSetting' | string } newValue
     * @param { groupData } metadata
     */
    changeEnumAttribute(state, { newValue, metadata }) {
      const target = state.attributes.find(attribute => attribute.name === metadata.parent)
      if (!target) {
        if (newValue === 'noSetting') return
        const obj = {
          name: metadata.parent,
          valueType: metadata.valueType,
        }
        obj.values = [newValue]
        state.attributes.push(obj)
        return
      }
      const newAttributes = []
      for (let i = 0; i < state.attributes.length; i++) {
        const attribute = cloneDeep(state.attributes[i])
        if (attribute.name !== target.name) {
          newAttributes.push(attribute)
          continue
        }
        if (newValue === 'noSetting') continue
        attribute.values = [newValue]
        newAttributes.push(attribute)
      }
      state.attributes = newAttributes
    },
    /**
     * @param state
     * @param { valueMetadata } valueMetadata
     * @param { groupData } metadata
     */
    changeRepeatedEnumAttribute(state, { metadata, valueMetadata }) {
      const target = state.attributes.find(attribute => attribute.name === metadata.parent)
      if (!target) {
        const obj = {
          name: metadata.parent,
          valueType: metadata.valueType,
          repeatedEnumValue: {
            setValues: [],
            unsetValues: [],
          }
        }
        obj.repeatedEnumValue.setValues = [valueMetadata.value]
        obj.repeatedEnumValue.unsetValues = []
        state.attributes.push(obj)
        return
      }
      const newAttributes = []
      for (let i = 0; i < state.attributes.length; i++) {
        const attribute = cloneDeep(state.attributes[i])
        if (attribute.name !== target.name) {
          newAttributes.push(attribute)
          continue
        }
        // 「○->×->設定なし」と切り替える
        if (metadata.repeatedEnumValue.setValues.includes(valueMetadata.value)) {
          attribute.repeatedEnumValue.unsetValues.push(valueMetadata.value)
          attribute.repeatedEnumValue.setValues = attribute.repeatedEnumValue.setValues.filter(v => v !== valueMetadata.value)
        } else if(metadata.repeatedEnumValue.unsetValues.includes(valueMetadata.value)) {
          attribute.repeatedEnumValue.unsetValues = attribute.repeatedEnumValue.unsetValues.filter(v => v !== valueMetadata.value)
        } else {
          attribute.repeatedEnumValue.setValues.push(valueMetadata.value)
        }
        // 操作が終わってどちらも空ならattributesから消す
        if (attribute.repeatedEnumValue.setValues.length === 0
          && attribute.repeatedEnumValue.unsetValues.length === 0
        ) {
          continue
        }
        newAttributes.push(attribute)
      }
      state.attributes = newAttributes
    },
    prepareConnect(state) {
      state.loading = true
    },
    readGmbAttributesSuccess(state, payload) {
      state.attributes = payload.data.attributes.map(v => castAttributesForCamelCase(v))
      state.attributeMetadata = payload.data.attribute_metadata.map(v => castAttributesForCamelCase(v))
      state.loading = false
    },
    updateGmbAttributesSuccess(state) {
      state.loading = false
    },
    apiFailure(state) {
      state.loading = false
    },
  },
  actions: {
    updateGmbAttribute({ commit, rootState }, payload) {
      commit('prepareConnect')

      return axios
        .put('/api/companies/' + rootState.route.params.company_uuid + '/stores/' + rootState.route.params.store_uuid + '/gmb/attributes', {
          attributes: payload.map(v => castAttributesForSnakeCase(v))
        })
        .then(() => {
          commit('updateGmbAttributesSuccess')
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('messages.updated', { value: i18n.t('models.gmbSiteSetting.modelName') }) }, { root: true })
          return true
        })
        .catch(err => {
          commit('apiFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
    reduceAttribute({ commit }, { type, payload }) {
      switch(type) {
      case gmbLocationAttributeValueTypes[0]:
        commit('changeBoolAttribute', payload)
        break
      case gmbLocationAttributeValueTypes[1]:
        commit('changeEnumAttribute', payload)
        break
      case gmbLocationAttributeValueTypes[2]:
        commit('changeRepeatedEnumAttribute', payload)
        break
      case gmbLocationAttributeValueTypes[3]:
        commit('changeUrlAttribute', payload)
        break
      }
    },
    readGmbAttributes({ commit, rootState }) {
      commit('prepareConnect')

      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/stores/' + rootState.route.params.store_uuid + '/gmb/attributes.json')
        .then(res => {
          commit('readGmbAttributesSuccess', res.data)
        })
        .catch(err => {
          commit('apiFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
  }
}
